import {
  ComponentProps,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  ControlledTreeEnvironment,
  InteractionMode,
  Tree,
  type TreeItemIndex,
  type TreeRef,
} from 'react-complex-tree'

import { PostType, Space } from '@bettermode/common/gql/generated'
import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { useFirstRenderContext } from '@tribeplatform/react-components/common/ssr'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { cn } from '@tribeplatform/react-components/utils/cn'
import { SimpleDivider } from '@tribeplatform/react-ui-kit/Divider'
import { useSearchQuery } from '@tribeplatform/react-ui-kit/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Input } from '@tribeplatform/react-ui-kit/Input'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { NavVertical, Sidebar } from '@tribeplatform/react-ui-kit/Sidebar'

import { SidebarBackButton } from '../../AdminMenu/SidebarBackButton.js'
import { AddCollection } from '../../SiteTree/components/AddCollection.js'
import { AddSpace } from '../../SiteTree/components/AddSpace.js'
import { TreeContainer } from '../../SiteTree/components/TreeContainer.js'
import { TreeItemArrow } from '../../SiteTree/components/TreeItemArrow.js'
import { TreeItemComponent } from '../../SiteTree/components/TreeItemComponent.js'
import { TreeItemsContainer } from '../../SiteTree/components/TreeItemsContainer.js'
import { TreeLiveDescriptorContainer } from '../../SiteTree/components/TreeLiveDescriptorContainer.js'
import {
  SiteTreeContextType,
  SiteTreeProvider,
} from '../../SiteTree/contexts/SiteTreeContext.js'
import { useCMSTreeItems } from '../../SiteTree/hooks/useCMSTreeItems.js'
import { useCollectionTreeItems } from '../../SiteTree/hooks/useCollectionTreeItems.js'
import { useScrollToTreeItem } from '../../SiteTree/hooks/useScrollToTreeItem.js'
import { useTreeViewState } from '../../SiteTree/hooks/useTreeViewState.js'
import { useUtilitySpacesTreeItems } from '../../SiteTree/hooks/useUtilitySpacesTreeItems.js'
import {
  COLLECTION_INDEX_PREFIX,
  collectionTreeItemIndex,
} from '../../SiteTree/tree-items/createCollectionTreeItem.js'
import { NO_COLLECTION_INDEX } from '../../SiteTree/tree-items/createNoCollectionTreeItem.js'
import {
  ROOT_INDEX,
  createRootTreeItem,
} from '../../SiteTree/tree-items/createRootTreeItem.js'
import { TreeItems } from '../../SiteTree/types.js'

const TREE_ID = 'site-tree'

const TREE_SETTINGS: SiteTreeContextType = {
  collection: {
    showMenu: false,
  },
  space: {
    to: (space: Space) => adminRoutes.design.customizer.spaceBlocks(space.id),
  },
  cms: {
    to: (postType: PostType) =>
      adminRoutes.design.customizer.cmsBlocks(postType.id),
    showMenu: false,
  },
}

interface TreeContentHandle {
  highlightCollection: (collectionId: string) => void
}

export function CustomizerSidebar({
  className,
  ...props
}: ComponentProps<typeof Sidebar>) {
  const { $t } = useI18n()
  const { query, inputProps } = useSearchQuery()
  const treeContentRef = useRef<TreeContentHandle>(null)

  return (
    <Sidebar size="lg" className={cn('h-full', className)} {...props}>
      <Sidebar.Content className="px-0">
        <div className="h-full flex flex-col gap-4">
          <div className="px-3">
            <SidebarBackButton href={adminRoutes.design.root} />
          </div>
          <div className="flex flex-col gap-4">
            <SectionHeader
              title={
                <T
                  id="Generics.Design.Customizer"
                  defaultMessage="Page customizer"
                />
              }
              description={
                <T
                  id="Generics.Design.Customizer.Description"
                  defaultMessage="Select pages to customize their layout and content"
                />
              }
              className="px-2 sm:ms-3"
            />

            <div className="px-3">
              <Input
                {...inputProps}
                id="search-tree-input"
                name="search-tree-input"
                placeholder={$t({
                  id: 'Generics.Search',
                  defaultMessage: 'Search',
                })}
              />
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <div
              className="h-full overflow-auto"
              style={{ scrollbarColor: '#939393 transparent' }}
            >
              <TreeContent ref={treeContentRef} query={query} />
            </div>
          </div>
          <NavVertical className="space-y-2">
            <SimpleDivider className="my-1" variant="subdued" />
            <AddSpace to={adminRoutes.design.createSpace()} />
            <AddCollection
              onSuccess={collection =>
                treeContentRef.current?.highlightCollection(collection.id)
              }
            />
          </NavVertical>
        </div>
      </Sidebar.Content>
    </Sidebar>
  )
}

const TreeContent = forwardRef<TreeContentHandle, { query?: string }>(
  ({ query }, ref) => {
    const treeRef = useRef<TreeRef>(null)
    const [highlightedCollectionId, setHighlightedCollectionId] = useState<
      string | null
    >(null)

    const highlightedIndex = highlightedCollectionId
      ? collectionTreeItemIndex(highlightedCollectionId)
      : undefined

    const { items, expandedItems } = useSiteTreeItems({
      query,
      highlightedCollection: highlightedIndex,
    })

    const { viewState, ...viewStateSetters } = useTreeViewState({
      initialExpanded: expandedItems,
      autoExpand: expandedItems,
    })
    const { isFirstRender } = useFirstRenderContext()

    const clearHighlight = useCallback(() => {
      setHighlightedCollectionId(null)
    }, [])

    useImperativeHandle(ref, () => ({
      highlightCollection: setHighlightedCollectionId,
    }))

    useScrollToTreeItem(treeRef, highlightedIndex, items, clearHighlight)

    if (isFirstRender) {
      return (
        <div className="flex items-center justify-center">
          <SvgIcon name="spinner" className="animate-spin" />
        </div>
      )
    }

    return (
      <SiteTreeProvider value={TREE_SETTINGS}>
        <ControlledTreeEnvironment
          {...viewStateSetters}
          viewState={{
            [TREE_ID]: viewState,
          }}
          items={items}
          getItemTitle={item => item.data.title}
          defaultInteractionMode={InteractionMode.ClickItemToExpand}
          renderItem={TreeItemComponent}
          renderItemArrow={TreeItemArrow}
          renderTreeContainer={TreeContainer}
          renderItemsContainer={TreeItemsContainer}
          renderLiveDescriptorContainer={TreeLiveDescriptorContainer}
        >
          <Tree
            ref={treeRef}
            treeId={TREE_ID}
            rootItem={ROOT_INDEX}
            treeLabel="Site"
          />
        </ControlledTreeEnvironment>
      </SiteTreeProvider>
    )
  },
)

function useSiteTreeItems({
  query,
  highlightedCollection,
}: {
  query?: string
  highlightedCollection?: TreeItemIndex
}): {
  items: TreeItems
  expandedItems: TreeItemIndex[]
} {
  const { collectionTreeItems, collectionIndex } = useCollectionTreeItems({
    query,
    activeCollectionIndex: highlightedCollection,
  })
  const { utilitySpacesTreeItems, utilitySpacesIndex } =
    useUtilitySpacesTreeItems({ query })

  const { cmsTreeItems, cmsIndex } = useCMSTreeItems({
    query,
  })

  const items: TreeItems = useMemo(
    () => ({
      root: createRootTreeItem({
        children: [collectionIndex, utilitySpacesIndex, cmsIndex],
      }),
      ...collectionTreeItems,
      ...utilitySpacesTreeItems,
      ...cmsTreeItems,
    }),
    [
      collectionTreeItems,
      cmsTreeItems,
      collectionIndex,
      cmsIndex,
      utilitySpacesTreeItems,
      utilitySpacesIndex,
    ],
  )

  const expandedItems = useMemo(() => {
    const expanded: TreeItemIndex[] = [collectionIndex]
    if (query) {
      for (const key of Object.keys(collectionTreeItems)) {
        if (
          key.startsWith(COLLECTION_INDEX_PREFIX) ||
          key === NO_COLLECTION_INDEX
        ) {
          expanded.push(key)
        }
      }
    }
    return expanded
  }, [query, collectionTreeItems, collectionIndex])

  return { items, expandedItems }
}
